import { createAsyncThunk, AnyAction } from "@reduxjs/toolkit"
import { ThunkDispatch } from "redux-thunk"

import { API } from "redux/api"
import type { AxiosError } from "axios"
import { ClientFormValues } from "types"
import i18n from "config/i18n"

import {
  ACTION_STATUS_SUCCESS,
  POLLING_INTERVAL,
  POLLING_TIMEOUT,
} from "config"

import { login } from "redux/features/auth/authSlice"

import {
  start as startPolling,
  reset as resetPolling,
} from "redux/features/polling/pollingSlice"

export const fetchClient = createAsyncThunk("client/fetch", async () => {
  const response = await API.get("/client")
  return response.data
})

export const updateClient = createAsyncThunk(
  "client/update",
  async (values: ClientFormValues, { rejectWithValue }) => {
    try {
      const response = await API.put("/client", values)
      if (response.status >= 400) {
        return rejectWithValue(response.data)
      } else {
        return response.data
      }
    } catch (err) {
      const error = err as AxiosError
      return rejectWithValue(error.message)
    }
  },
)

export const fetchClientLogInSession = createAsyncThunk(
  "client-log-in-session/fetch",
  async (session_id: string) => {
    const response = await API.get(`/client/log_in/${session_id}`)

    return response.data
  },
)

export const makeUpdateClient = (values: ClientFormValues) => {
  return function (dispatch: ThunkDispatch<unknown, unknown, AnyAction>) {
    return new Promise((resolve, reject) => {
      return dispatch(updateClient(values))
        .then(
          ({ payload }) => {
            if (!payload) {
              return reject(i18n.t("features.client.update.unavailable"))
            } else if (payload["error"]) {
              return reject(payload["error"])
            } else {
              return resolve(i18n.t("features.client.update.success"))
            }
          },
          () => {
            reject(i18n.t("features.client.update.failure"))
          },
        )
        .catch((error) => {
          reject(error)
        })
    })
  }
}

export const makeClientSignIn = (session_id: string) => {
  return function (dispatch: ThunkDispatch<unknown, unknown, AnyAction>) {
    return new Promise((resolve) => {
      const timerId = setInterval(async () => {
        const result = await dispatch(fetchClientLogInSession(session_id))

        if (result?.payload?.log_in) {
          const { status, action_payload } = result.payload.log_in

          if (status === ACTION_STATUS_SUCCESS) {
            clearInterval(timerId)
            dispatch(resetPolling())

            const { access_token, refresh_token, client_id } = action_payload

            dispatch(
              login({
                id: client_id,
                accessToken: access_token,
                refreshToken: refresh_token,
              }),
            )

            resolve("success")
          }
        }
      }, POLLING_INTERVAL)

      dispatch(startPolling({ pollingId: timerId }))

      setTimeout(() => {
        clearInterval(timerId)
        dispatch(resetPolling())
      }, POLLING_TIMEOUT)
    })
  }
}
